import { CodeIcon, GlobeIcon, ListIcon, XIcon } from '@phosphor-icons/react'
import { CodeFile, type EditorError } from 'mini-code'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BytecodeCompiler } from './bytecode-compiler.ts'
import { type AmplitudeInfo, type LevelInfo, type NumberWithParamsInfo, type PatternEventInfo, type SequenceData,
  type TimelineInfo, type WaveformInfo } from './bytecode.ts'
import { dspscript } from './dsp-script.ts'
import { convertToEditorErrors } from './editor-errors.ts'
import { InlineEditor } from './InlineEditor.tsx'
import { renderMarkdownProse } from './markdown-renderer.tsx'
import { NavBar } from './NavBar.tsx'
import { Link, navigate } from './Router.tsx'
import { useCompileModeStore, useLatencyHintStore, useNavBarHeightStore } from './store.ts'
import { type ParsedTutorial, parseTutorialMarkdown } from './tutorial-parser.ts'
import { getAllTutorials, getTutorial } from './tutorials/index.ts'

const compiler = new BytecodeCompiler()

interface TutorialProps {
  markdown: string
  tutorialId?: string
}

export function Tutorial({ markdown, tutorialId }: TutorialProps) {
  const { height: navBarHeight } = useNavBarHeightStore()
  const { latencyHint } = useLatencyHintStore()
  const { compileMode } = useCompileModeStore()

  const parsed = useMemo(() => parseTutorialMarkdown(markdown), [markdown])

  type SectionId = string
  type CodeId = string

  const [playingCodeId, setPlayingCodeId] = useState<CodeId | null>(null)
  const [errors, setErrors] = useState<Record<CodeId, EditorError[]>>({})
  const [errorMessages, setErrorMessages] = useState<Record<CodeId, string>>({})
  const [numbersWithParams, setNumbersWithParams] = useState<Record<CodeId, NumberWithParamsInfo[]>>({})
  const [patternEvents, setPatternEvents] = useState<Record<CodeId, PatternEventInfo[]>>({})
  const [sequences, setSequences] = useState<Record<CodeId, SequenceData[]>>({})
  const [amplitudeInfos, setAmplitudeInfos] = useState<Record<CodeId, AmplitudeInfo[]>>({})
  const [waveformInfos, setWaveformInfos] = useState<Record<CodeId, WaveformInfo[]>>({})
  const [levelInfos, setLevelInfos] = useState<Record<CodeId, LevelInfo[]>>({})
  const [timelineInfos, setTimelineInfos] = useState<Record<CodeId, TimelineInfo[]>>({})
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const [isTocOpen, setIsTocOpen] = useState(false)
  const [sectionCodeIdsBySectionId, codeFiles] = useMemo(() => {
    const sectionCodeIds: Record<SectionId, CodeId[]> = {}
    const files: Record<CodeId, CodeFile> = {}

    for (const section of parsed.sections) {
      const codes = section.codes && section.codes.length > 0
        ? section.codes
        : section.code
        ? [section.code]
        : []

      if (codes.length === 0) continue

      const codeIds: CodeId[] = []

      codes.forEach((code, index) => {
        const codeId = index === 0 ? section.id : `${section.id}__${index}`
        codeIds.push(codeId)
        files[codeId] = new CodeFile(code)
      })

      sectionCodeIds[section.id] = codeIds
    }

    return [sectionCodeIds, files] as const
  }, [parsed])

  // Build hierarchical structure: H2 sections with their H3 subsections, and H3 with H4 subsections
  const tocStructure = useMemo(() => {
    type H3Subsection = { id: string; label: string; h4Subsections: Array<{ id: string; label: string }> }
    type H2Section = { id: string; label: string; subsections: H3Subsection[] }

    const h2Sections: H2Section[] = []
    const h3SectionMap = new Map<string, H3Subsection>()

    // First pass: identify H2 and H3 sections
    for (const section of parsed.sections) {
      const isH3 = parsed.sections.some(s => s.id !== section.id && section.id.startsWith(s.id + '-'))
      const isH4 = parsed.sections.some(s => {
        if (s.id === section.id) return false
        const otherIsH3 = parsed.sections.some(s2 => s2.id !== s.id && s.id.startsWith(s2.id + '-'))
        return otherIsH3 && section.id.startsWith(s.id + '-')
      })

      if (!isH3 && !isH4) {
        // This is an H2 section
        const h2Section: H2Section = {
          id: section.id,
          label: section.title,
          subsections: [],
        }
        h2Sections.push(h2Section)
      }
      else if (isH3 && !isH4) {
        // This is an H3 section
        const h3Subsection: H3Subsection = {
          id: section.id,
          label: section.title,
          h4Subsections: [],
        }
        h3SectionMap.set(section.id, h3Subsection)
      }
    }

    // Second pass: add H3 subsections to their parent H2 sections
    for (const section of parsed.sections) {
      const isH3 = parsed.sections.some(s => s.id !== section.id && section.id.startsWith(s.id + '-'))
      const isH4 = parsed.sections.some(s => {
        if (s.id === section.id) return false
        const otherIsH3 = parsed.sections.some(s2 => s2.id !== s.id && s.id.startsWith(s2.id + '-'))
        return otherIsH3 && section.id.startsWith(s.id + '-')
      })

      if (isH3 && !isH4) {
        // Find the parent H2 section
        for (const h2Section of h2Sections) {
          if (section.id.startsWith(h2Section.id + '-')) {
            const h3Subsection = h3SectionMap.get(section.id)
            if (h3Subsection) {
              h2Section.subsections.push(h3Subsection)
            }
            break
          }
        }
      }
    }

    // Third pass: add H4 subsections to their parent H3 sections
    for (const section of parsed.sections) {
      const isH4 = parsed.sections.some(s => {
        if (s.id === section.id) return false
        const otherIsH3 = parsed.sections.some(s2 => s2.id !== s.id && s.id.startsWith(s2.id + '-'))
        return otherIsH3 && section.id.startsWith(s.id + '-')
      })

      if (isH4) {
        // Find the parent H3 section
        for (const h3Subsection of h3SectionMap.values()) {
          if (section.id.startsWith(h3Subsection.id + '-')) {
            h3Subsection.h4Subsections.push({ id: section.id, label: section.title })
            break
          }
        }
      }
    }

    return h2Sections
  }, [parsed])

  const tocSections = useMemo(
    () => parsed.sections.map(s => ({ id: s.id, label: s.title })),
    [parsed],
  )

  // Determine which H2 section is currently active
  const activeH2Section = useMemo(() => {
    if (!activeSection) return null

    // Check if the active section is an H2 section itself
    const isH2 = tocStructure.some(h2 => h2.id === activeSection)
    if (isH2) return activeSection

    // Otherwise, find which H2 section contains this H3 or H4
    for (const h2Section of tocStructure) {
      if (activeSection.startsWith(h2Section.id + '-')) {
        return h2Section.id
      }
    }

    return null
  }, [activeSection, tocStructure])

  // Determine which H3 section is currently active (for expanding H4 subsections)
  const activeH3Section = useMemo(() => {
    if (!activeSection) return null

    // Check if the active section is an H3 section itself
    for (const h2Section of tocStructure) {
      for (const h3Subsection of h2Section.subsections) {
        if (h3Subsection.id === activeSection) {
          return h3Subsection.id
        }
      }
    }

    // Check if the active section is an H4, find its parent H3
    for (const h2Section of tocStructure) {
      for (const h3Subsection of h2Section.subsections) {
        if (activeSection.startsWith(h3Subsection.id + '-')) {
          return h3Subsection.id
        }
      }
    }

    return null
  }, [activeSection, tocStructure])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.scrollTo({ top: 0 })
    }
    window.scrollTo({ top: 0 })
  }, [tutorialId, markdown])

  useEffect(() => {
    const hash = window.location.hash.slice(1)
    if (hash && scrollContainerRef.current) {
      const element = document.getElementById(hash)
      if (element) {
        setTimeout(() => {
          const container = scrollContainerRef.current
          if (container) {
            const elementRect = element.getBoundingClientRect()
            const containerRect = container.getBoundingClientRect()
            const scrollTop = elementRect.top - containerRect.top + container.scrollTop - 20
            container.scrollTo({
              top: scrollTop,
              behavior: 'smooth',
            })
          }
        }, 100)
      }
    }
  }, [parsed])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const sections = tocSections
        .map(s => document.getElementById(s.id))
        .filter(Boolean) as HTMLElement[]
      if (sections.length === 0) return

      const scrollTop = container.scrollTop
      const offset = 150

      let currentSection = tocSections[0]?.id || null
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i]
        const containerRect = container.getBoundingClientRect()
        const sectionRect = section.getBoundingClientRect()
        const sectionTop = sectionRect.top - containerRect.top + scrollTop

        if (scrollTop + offset >= sectionTop) {
          currentSection = tocSections[i]?.id || null
        }
        else {
          break
        }
      }

      setActiveSection(currentSection)
    }

    container.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [tocSections])

  useEffect(() => {
    const interval = setInterval(() => {
      const actualPlaying = dspscript.isPlaying()
      if (!actualPlaying && playingCodeId !== null) {
        setPlayingCodeId(null)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [playingCodeId])

  const handlePlay = useCallback(
    async (codeId: CodeId) => {
      try {
        setErrors(prev => ({ ...prev, [codeId]: [] }))
        setErrorMessages(prev => ({ ...prev, [codeId]: '' }))
        if (!dspscript.getAudioContext()) {
          await dspscript.init(latencyHint)
        }
        const code = codeFiles[codeId]?.value || ''
        const currentGlobalBpm = 120
        try {
          const program = compiler.compile(code, currentGlobalBpm)
          setNumbersWithParams(prev => ({ ...prev, [codeId]: program.numbersWithParams || [] }))
          setPatternEvents(prev => ({ ...prev, [codeId]: program.patternEvents || [] }))
          setSequences(prev => ({ ...prev, [codeId]: program.sequences || [] }))
        }
        catch (err: any) {
          setNumbersWithParams(prev => ({ ...prev, [codeId]: [] }))
          setPatternEvents(prev => ({ ...prev, [codeId]: [] }))
          setSequences(prev => ({ ...prev, [codeId]: [] }))
        }
        await dspscript.loadNewProgram(code)
        await dspscript.start()
        setPlayingCodeId(codeId)
      }
      catch (err: any) {
        const editorErrors = convertToEditorErrors(err, codeFiles[codeId]?.value || '')
        setErrors(prev => ({ ...prev, [codeId]: editorErrors }))
        setErrorMessages(prev => ({
          ...prev,
          [codeId]: editorErrors[0]?.message || err.message,
        }))
        setNumbersWithParams(prev => ({ ...prev, [codeId]: [] }))
        setPatternEvents(prev => ({ ...prev, [codeId]: [] }))
        setSequences(prev => ({ ...prev, [codeId]: [] }))
        setAmplitudeInfos(prev => ({ ...prev, [codeId]: [] }))
        setWaveformInfos(prev => ({ ...prev, [codeId]: [] }))
        setLevelInfos(prev => ({ ...prev, [codeId]: [] }))
        setTimelineInfos(prev => ({ ...prev, [codeId]: [] }))
        setPlayingCodeId(null)
      }
    },
    [codeFiles, latencyHint],
  )

  const handleStop = useCallback(async () => {
    try {
      await dspscript.stop()
      setPlayingCodeId(null)
    }
    catch (err: any) {
      const currentCodeId = playingCodeId
      if (currentCodeId && codeFiles[currentCodeId]) {
        const editorErrors = convertToEditorErrors(err, codeFiles[currentCodeId].value)
        setErrors(prev => ({ ...prev, [currentCodeId]: editorErrors }))
        setErrorMessages(prev => ({
          ...prev,
          [currentCodeId]: editorErrors[0]?.message || err.message,
        }))
      }
    }
  }, [playingCodeId, codeFiles])

  const handleCompileCheck = useCallback((code: string, codeId: CodeId) => {
    try {
      const currentGlobalBpm = 120
      const program = compiler.compile(code, currentGlobalBpm)
      setErrors(prev => ({ ...prev, [codeId]: [] }))
      setErrorMessages(prev => ({ ...prev, [codeId]: '' }))
      setNumbersWithParams(prev => ({ ...prev, [codeId]: program.numbersWithParams || [] }))
      setPatternEvents(prev => ({ ...prev, [codeId]: program.patternEvents || [] }))
      setSequences(prev => ({ ...prev, [codeId]: program.sequences || [] }))
      setAmplitudeInfos(prev => ({ ...prev, [codeId]: program.amplitudeInfos || [] }))
      setWaveformInfos(prev => ({ ...prev, [codeId]: program.waveformInfos || [] }))
      setLevelInfos(prev => ({ ...prev, [codeId]: program.levelInfos || [] }))
      setTimelineInfos(prev => ({ ...prev, [codeId]: program.timelineInfos || [] }))
    }
    catch (err: any) {
      const editorErrors = convertToEditorErrors(err, code)
      setErrors(prev => ({ ...prev, [codeId]: editorErrors }))
      setErrorMessages(prev => ({
        ...prev,
        [codeId]: editorErrors[0]?.message || err.message,
      }))
      setNumbersWithParams(prev => ({ ...prev, [codeId]: [] }))
      setPatternEvents(prev => ({ ...prev, [codeId]: [] }))
      setSequences(prev => ({ ...prev, [codeId]: [] }))
      setAmplitudeInfos(prev => ({ ...prev, [codeId]: [] }))
      setWaveformInfos(prev => ({ ...prev, [codeId]: [] }))
      setLevelInfos(prev => ({ ...prev, [codeId]: [] }))
      setTimelineInfos(prev => ({ ...prev, [codeId]: [] }))
    }
  }, [])

  const handleCompile = useCallback(
    async (code: string, codeId: CodeId) => {
      if (playingCodeId !== codeId) return

      try {
        const currentGlobalBpm = 120
        try {
          const program = compiler.compile(code, currentGlobalBpm)
          setNumbersWithParams(prev => ({ ...prev, [codeId]: program.numbersWithParams || [] }))
          setPatternEvents(prev => ({ ...prev, [codeId]: program.patternEvents || [] }))
          setSequences(prev => ({ ...prev, [codeId]: program.sequences || [] }))
          setAmplitudeInfos(prev => ({ ...prev, [codeId]: program.amplitudeInfos || [] }))
          setWaveformInfos(prev => ({ ...prev, [codeId]: program.waveformInfos || [] }))
          setLevelInfos(prev => ({ ...prev, [codeId]: program.levelInfos || [] }))
          setTimelineInfos(prev => ({ ...prev, [codeId]: program.timelineInfos || [] }))
        }
        catch (err: any) {
          setNumbersWithParams(prev => ({ ...prev, [codeId]: [] }))
          setPatternEvents(prev => ({ ...prev, [codeId]: [] }))
          setSequences(prev => ({ ...prev, [codeId]: [] }))
          setAmplitudeInfos(prev => ({ ...prev, [codeId]: [] }))
          setWaveformInfos(prev => ({ ...prev, [codeId]: [] }))
          setLevelInfos(prev => ({ ...prev, [codeId]: [] }))
          setTimelineInfos(prev => ({ ...prev, [codeId]: [] }))
        }
        // Use hot-reload when already playing to preserve state
        await dspscript.loadProgram(code)
        setErrors(prev => ({ ...prev, [codeId]: [] }))
        setErrorMessages(prev => ({ ...prev, [codeId]: '' }))
      }
      catch (err: any) {
        const editorErrors = convertToEditorErrors(err, code)
        setErrors(prev => ({ ...prev, [codeId]: editorErrors }))
        setErrorMessages(prev => ({
          ...prev,
          [codeId]: editorErrors[0]?.message || err.message,
        }))
        setNumbersWithParams(prev => ({ ...prev, [codeId]: [] }))
        setPatternEvents(prev => ({ ...prev, [codeId]: [] }))
        setSequences(prev => ({ ...prev, [codeId]: [] }))
        setAmplitudeInfos(prev => ({ ...prev, [codeId]: [] }))
        setWaveformInfos(prev => ({ ...prev, [codeId]: [] }))
        setLevelInfos(prev => ({ ...prev, [codeId]: [] }))
        setTimelineInfos(prev => ({ ...prev, [codeId]: [] }))
      }
    },
    [playingCodeId],
  )

  const handleTocClick = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId)
    const container = scrollContainerRef.current
    if (element && container) {
      const elementRect = element.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const scrollTop = elementRect.top - containerRect.top + container.scrollTop - 20
      container.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      })
      window.history.replaceState(null, '', `#${sectionId}`)
      setIsTocOpen(false)
    }
  }, [])

  const onCompileCallbacks = useMemo(() => {
    const callbacks: Record<CodeId, (code: string) => Promise<void>> = {}
    for (const [sectionId, codeIds] of Object.entries(sectionCodeIdsBySectionId)) {
      for (const codeId of codeIds) {
        callbacks[codeId] = (code: string) => handleCompile(code, codeId)
      }
    }
    return callbacks
  }, [sectionCodeIdsBySectionId, handleCompile])

  const onCompileCheckCallbacks = useMemo(() => {
    const callbacks: Record<CodeId, (code: string) => void> = {}
    for (const codeIds of Object.values(sectionCodeIdsBySectionId)) {
      for (const codeId of codeIds) {
        callbacks[codeId] = (code: string) => handleCompileCheck(code, codeId)
      }
    }
    return callbacks
  }, [sectionCodeIdsBySectionId, handleCompileCheck])

  const onPlayCallbacks = useMemo(() => {
    const callbacks: Record<CodeId, () => Promise<void>> = {}
    for (const codeIds of Object.values(sectionCodeIdsBySectionId)) {
      for (const codeId of codeIds) {
        callbacks[codeId] = () => handlePlay(codeId)
      }
    }
    return callbacks
  }, [sectionCodeIdsBySectionId, handlePlay])

  const onErrorsChangeCallbacks = useMemo(() => {
    const callbacks: Record<CodeId, (newErrors: EditorError[]) => void> = {}
    for (const codeIds of Object.values(sectionCodeIdsBySectionId)) {
      for (const codeId of codeIds) {
        callbacks[codeId] = (newErrors: EditorError[]) => {
          setErrors(prev => ({ ...prev, [codeId]: newErrors }))
        }
      }
    }
    return callbacks
  }, [sectionCodeIdsBySectionId])

  const onErrorMessageChangeCallbacks = useMemo(() => {
    const callbacks: Record<CodeId, (message: string) => void> = {}
    for (const codeIds of Object.values(sectionCodeIdsBySectionId)) {
      for (const codeId of codeIds) {
        callbacks[codeId] = (message: string) => {
          setErrorMessages(prev => ({ ...prev, [codeId]: message }))
        }
      }
    }
    return callbacks
  }, [sectionCodeIdsBySectionId])

  const renderEditor = useCallback(
    (codeId: CodeId) => {
      if (!codeFiles[codeId]) return null
      return (
        <InlineEditor
          codeFile={codeFiles[codeId]}
          errors={errors[codeId] || []}
          errorMessage={errorMessages[codeId] || ''}
          isPlaying={playingCodeId === codeId}
          isActive={playingCodeId === codeId}
          compileMode={compileMode}
          onCompile={onCompileCallbacks[codeId]}
          onCompileCheck={onCompileCheckCallbacks[codeId]}
          showPlayStop={true}
          showVisualizer={true}
          onPlay={onPlayCallbacks[codeId]}
          onStop={handleStop}
          autoHeight={true}
          onErrorsChange={onErrorsChangeCallbacks[codeId]}
          onErrorMessageChange={onErrorMessageChangeCallbacks[codeId]}
          numbersWithParams={numbersWithParams[codeId] || []}
          patternEvents={patternEvents[codeId] || []}
          sequences={sequences[codeId] || []}
          amplitudeInfos={amplitudeInfos[codeId] || []}
          waveformInfos={waveformInfos[codeId] || []}
          levelInfos={levelInfos[codeId] || []}
          timelineInfos={timelineInfos[codeId] || []}
          contextId={codeId}
        />
      )
    },
    [
      codeFiles,
      errors,
      errorMessages,
      playingCodeId,
      compileMode,
      onCompileCallbacks,
      onCompileCheckCallbacks,
      onPlayCallbacks,
      handleStop,
      onErrorsChangeCallbacks,
      onErrorMessageChangeCallbacks,
      numbersWithParams,
      patternEvents,
      sequences,
      amplitudeInfos,
    ],
  )

  const rightSideButtons = (
    <>
      <Link
        href="/"
        className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-lime-400 to-green-600 text-white hover:opacity-90 no-underline flex items-center gap-2"
      >
        <GlobeIcon weight="regular" size={16} />
        <span className="hidden md:inline">Browse</span>
      </Link>
      <Link
        href="/editor"
        className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-orange-500 to-red-600 text-white hover:opacity-90 no-underline flex items-center gap-2"
      >
        <CodeIcon weight="regular" size={16} />
        <span className="hidden md:inline">Switch to Editor</span>
        <span className="md:hidden">Editor</span>
      </Link>
    </>
  )

  const firstCodeId = useMemo(() => {
    for (const section of parsed.sections) {
      const codeIds = sectionCodeIdsBySectionId[section.id]
      if (codeIds && codeIds.length > 0) {
        return codeIds[0]
      }
    }
    return null
  }, [parsed.sections, sectionCodeIdsBySectionId])

  return (
    <div className="w-[100dvw] h-[100dvh] overflow-hidden text-[#f0f0f0] flex flex-col relative">
      <NavBar
        onPlay={firstCodeId ? () => handlePlay(firstCodeId) : undefined}
        onStop={handleStop}
        rightSideButtons={rightSideButtons}
      />
      <div className="relative flex" style={{ height: `calc(100dvh - ${navBarHeight}px)` }}>
        <button
          onClick={() => setIsTocOpen(true)}
          className="md:hidden fixed bottom-4 right-4 z-10 p-3 rounded-full bg-gradient-to-br from-lime-400 to-green-600 text-white shadow-lg hover:opacity-90"
          aria-label="Open table of contents"
        >
          <ListIcon weight="regular" size={24} />
        </button>
        {isTocOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-20"
            onClick={() => setIsTocOpen(false)}
          />
        )}
        <aside
          className={`fixed right-0 top-0 w-64 h-full border-l border-[#333] bg-[#1a1a1a] overflow-y-auto z-30 transition-transform duration-300 ${
            isTocOpen ? 'translate-x-0' : 'translate-x-full'
          } md:translate-x-0`}
          style={{ top: `${navBarHeight}px`, height: `calc(100dvh - ${navBarHeight}px)` }}
        >
          <div className="md:hidden flex items-center justify-between p-4 border-b border-[#333]">
            <h3 className="text-lg font-semibold text-lime-400 font-[Turret_Road] uppercase tracking-wider">
              Table of Contents
            </h3>
            <button
              onClick={() => setIsTocOpen(false)}
              className="p-2 hover:bg-[#2a2a2a] rounded"
              aria-label="Close table of contents"
            >
              <XIcon weight="regular" size={20} className="text-neutral-400" />
            </button>
          </div>
          <div className="p-6">
            <h3 className="hidden md:block text-lg font-semibold text-lime-400 mb-4 font-[Turret_Road] uppercase tracking-wider">
              Table of Contents
            </h3>
            <nav className="space-y-1">
              {tocStructure.map(h2Section => {
                const isH2Active = activeH2Section === h2Section.id
                const hasSubsections = h2Section.subsections.length > 0

                return (
                  <div key={h2Section.id}>
                    <button
                      onClick={() => handleTocClick(h2Section.id)}
                      className={`w-full text-left px-3 py-2 rounded font-semibold hover:bg-[#2a2a2a] ${
                        activeSection === h2Section.id
                          ? 'text-lime-400 bg-[#2a2a2a] font-medium'
                          : 'text-neutral-400'
                      }`}
                    >
                      {h2Section.label}
                    </button>
                    {hasSubsections && (
                      <div
                        className={`overflow-hidden transition-all duration-200 ${
                          isH2Active ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="pl-4 space-y-1 mt-1">
                          {h2Section.subsections.map(h3Subsection => {
                            const isH3Active = activeH3Section === h3Subsection.id
                            const hasH4Subsections = h3Subsection.h4Subsections.length > 0

                            return (
                              <div key={h3Subsection.id}>
                                <button
                                  onClick={() => handleTocClick(h3Subsection.id)}
                                  className={`w-full text-left px-3 py-1.5 rounded text-sm hover:bg-[#2a2a2a] ${
                                    activeSection === h3Subsection.id
                                      ? 'text-lime-400 bg-[#2a2a2a] font-medium'
                                      : 'text-neutral-500'
                                  }`}
                                >
                                  {h3Subsection.label}
                                </button>
                                {hasH4Subsections && (
                                  <div
                                    className={`overflow-hidden transition-all duration-200 ${
                                      isH3Active ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                                    }`}
                                  >
                                    <div className="pl-4 space-y-1 mt-1">
                                      {h3Subsection.h4Subsections.map(h4Subsection => (
                                        <button
                                          key={h4Subsection.id}
                                          onClick={() => handleTocClick(h4Subsection.id)}
                                          className={`w-full text-left px-3 py-1 rounded text-xs hover:bg-[#2a2a2a] ${
                                            activeSection === h4Subsection.id
                                              ? 'text-lime-400 bg-[#2a2a2a] font-medium'
                                              : 'text-neutral-500'
                                          }`}
                                        >
                                          {h4Subsection.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </nav>
          </div>
        </aside>
        <div
          ref={scrollContainerRef}
          className="relative h-full overflow-y-auto overflow-x-hidden px-6 py-4 md:p-10 flex-1 md:mr-64"
        >
          <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
            <div className="prose prose-invert max-w-none">
              <h1 className="text-2xl md:text-4xl font-extrabold font-[Turret_Road] text-lime-400 mb-4 md:mb-6">
                {parsed.title}
              </h1>
              {parsed.intro && (
                <div className="text-base md:text-lg text-[#e0e0e0] leading-relaxed mb-6 md:mb-8">
                  {renderMarkdownProse(parsed.intro)}
                </div>
              )}
            </div>

            {parsed.sections.map(section => {
              const isH3 = parsed.sections.some(
                s => s.id !== section.id && section.id.startsWith(s.id + '-'),
              )
              const isH4 = parsed.sections.some(s => {
                if (s.id === section.id) return false
                const otherIsH3 = parsed.sections.some(s2 => s2.id !== s.id && s.id.startsWith(s2.id + '-'))
                return otherIsH3 && section.id.startsWith(s.id + '-')
              })

              return (
                <div key={section.id}>
                  <div className="prose prose-invert max-w-none">
                    {isH4
                      ? (
                        <h4 className="text-base md:text-lg font-semibold text-lime-300 mb-2 md:mb-3 mt-4 md:mt-6 group relative font-[Turret_Road]">
                          <a href={`#${section.id}`} id={section.id} className="flex items-center">
                            <span className="hidden md:block opacity-0 group-hover:opacity-100 transition-opacity absolute -left-6 text-lime-400">
                              #
                            </span>
                            {section.title}
                          </a>
                        </h4>
                      )
                      : isH3
                        ? (
                            <h3 className="text-lg md:text-xl font-semibold text-lime-300 mb-2 md:mb-3 group relative font-[Turret_Road]">
                              <a href={`#${section.id}`} id={section.id} className="flex items-center">
                                <span className="hidden md:block opacity-0 group-hover:opacity-100 transition-opacity absolute -left-6 text-lime-400">
                                  #
                                </span>
                                {section.title}
                              </a>
                            </h3>
                          )
                        : (
                            <h2 className="text-2xl md:text-3xl font-bold text-lime-400 mb-3 md:mb-4 group relative font-[Turret_Road]">
                              <a href={`#${section.id}`} id={section.id} className="flex items-center">
                                <span className="hidden md:block opacity-0 group-hover:opacity-100 transition-opacity absolute -left-6 text-lime-400">
                                  #
                                </span>
                                {section.title}
                              </a>
                            </h2>
                          )}
                    {!section.blocks && section.intro && (
                      <div className="mb-4">{renderMarkdownProse(section.intro)}</div>
                    )}
                  </div>

                  {section.blocks && section.blocks.length > 0
                    ? (() => {
                      const codeIds = sectionCodeIdsBySectionId[section.id] || []
                      let codeIndex = 0
                      return (
                        <div className="space-y-4 mt-2">
                          {section.blocks.map((block, i) => {
                            if (block.type === 'prose') {
                              return (
                                <div key={`${section.id}-prose-${i}`} className="prose prose-invert max-w-none">
                                  {renderMarkdownProse(block.text)}
                                </div>
                              )
                            }
                            const codeId = codeIds[codeIndex]
                            codeIndex += 1
                            if (!codeId) return null
                            return (
                              <div key={codeId} className="mt-2">
                                {renderEditor(codeId)}
                              </div>
                            )
                          })}
                        </div>
                      )
                    })()
                    : (() => {
                      const codeIds = sectionCodeIdsBySectionId[section.id] || []
                      return (
                        <>
                          {codeIds.length > 0 && (
                            <div className="space-y-4 mt-2">
                              {codeIds.map(codeId => <div key={codeId}>{renderEditor(codeId)}</div>)}
                            </div>
                          )}
                          {section.explanation && (
                            <div className="prose prose-invert max-w-none mt-4">
                              {renderMarkdownProse(section.explanation)}
                            </div>
                          )}
                        </>
                      )
                    })()}
                </div>
              )
            })}

            {(() => {
              const allTutorials = getAllTutorials()
              const otherTutorials = tutorialId
                ? allTutorials.filter(t => t.id !== tutorialId).slice(0, 3)
                : allTutorials.slice(0, 3)

              if (otherTutorials.length === 0) {
                return (
                  <div className="text-center pt-8 pb-5">
                    <Link
                      href="/tutorials"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-br from-lime-400 to-green-600 text-white font-semibold hover:opacity-90 no-underline"
                    >
                      <CodeIcon weight="regular" size={20} />
                      Read more Tutorials
                    </Link>
                  </div>
                )
              }

              return (
                <div className="pt-8 pb-5">
                  <div className="prose prose-invert max-w-none mb-4 md:mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-lime-400 mb-3 md:mb-4 font-[Turret_Road]">
                      Continue Learning
                    </h2>
                    <p className="text-[#e0e0e0] leading-relaxed mb-4 md:mb-6 text-sm md:text-base">
                      Explore more tutorials to deepen your understanding of loopmaster.
                    </p>
                  </div>
                  <div className="grid gap-4 md:grid-cols-3 mb-6">
                    {otherTutorials.map(tutorial => (
                      <button
                        key={tutorial.id}
                        onClick={() => navigate(`/tutorials/${tutorial.id}`)}
                        className="text-left p-4 rounded-lg border border-[#333] bg-[#1a1a1a] hover:bg-[#2a2a2a] hover:border-lime-400 transition-colors group"
                      >
                        <h3 className="text-lg font-bold text-lime-400 mb-2 font-[Turret_Road] group-hover:text-lime-300">
                          {tutorial.title}
                        </h3>
                        {tutorial.description && (
                          <p className="text-[#e0e0e0] text-sm leading-relaxed">
                            {tutorial.description}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="text-center">
                    <Link
                      href="/tutorials"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-br from-lime-400 to-green-600 text-white font-semibold hover:opacity-90 no-underline"
                    >
                      <CodeIcon weight="regular" size={20} />
                      View All Tutorials
                    </Link>
                  </div>
                </div>
              )
            })()}

            <div className="text-center pt-4 pb-5 text-[#888] flex items-center justify-center gap-3">
              <CodeIcon size={20} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
